import { useRef, useState, useEffect, useCallback } from 'react';

interface UseOptimizedTextClampingOptions {
  debounceMs?: number;
  threshold?: number;
}

interface UseOptimizedTextClampingReturn {
  ref: React.RefObject<HTMLElement>;
  isClamped: boolean;
  isLoading: boolean;
}

// Cache for element measurements to avoid redundant DOM reads
const measurementCache = new WeakMap<
  HTMLElement,
  {
    isClamped: boolean;
    timestamp: number;
    signature: string;
  }
>();

// Generate a signature for the element's current state
const getElementSignature = (element: HTMLElement): string => {
  const computedStyle = window.getComputedStyle(element);
  return `${element.textContent?.length || 0}-${computedStyle.width}-${computedStyle.maxHeight}-${computedStyle.webkitLineClamp}`;
};

// Lightweight text clamping check
const checkIsTextClamped = (element: HTMLElement): boolean => {
  if (!element) return false;

  const now = performance.now();
  const signature = getElementSignature(element);

  // Check cache first (valid for 200ms)
  const cached = measurementCache.get(element);
  if (cached && now - cached.timestamp < 200 && cached.signature === signature) {
    return cached.isClamped;
  }

  let isClamped = false;

  // Try CSS-based detection first (faster)
  const computedStyle = window.getComputedStyle(element);
  const webkitLineClamp = computedStyle.webkitLineClamp;

  if (webkitLineClamp && webkitLineClamp !== 'none') {
    // For -webkit-line-clamp, we can use a more efficient check
    const lineHeight = parseFloat(computedStyle.lineHeight);
    const maxHeight = parseFloat(computedStyle.maxHeight);

    if (!isNaN(lineHeight) && !isNaN(maxHeight)) {
      const expectedHeight = lineHeight * parseInt(webkitLineClamp);
      isClamped = expectedHeight < element.scrollHeight;
    } else {
      // Fallback to scrollHeight comparison, but only if CSS method fails
      isClamped = element.scrollHeight > element.clientHeight;
    }
  } else {
    // For other overflow scenarios
    const maxHeight = parseFloat(computedStyle.maxHeight);
    if (!isNaN(maxHeight) && maxHeight > 0) {
      isClamped = element.scrollHeight > maxHeight;
    } else {
      isClamped = element.scrollHeight > element.clientHeight;
    }
  }

  // Cache the result
  measurementCache.set(element, {
    isClamped,
    timestamp: now,
    signature,
  });

  return isClamped;
};

export const useOptimizedTextClamping = ({
  debounceMs = 100,
  threshold = 2,
}: UseOptimizedTextClampingOptions = {}): UseOptimizedTextClampingReturn => {
  const ref = useRef<HTMLElement>(null);
  const [isClamped, setIsClamped] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const resizeObserverRef = useRef<ResizeObserver>();

  // Simple check function - no RAF, just direct execution
  const performCheck = useCallback(() => {
    if (!ref.current) {
      setIsLoading(false);
      return;
    }

    // Do the check directly - it's fast enough with caching
    const clamped = checkIsTextClamped(ref.current);
    setIsClamped(clamped);
    setIsLoading(false);
  }, []);

  // Debounced check function
  const debouncedCheck = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      performCheck();
    }, debounceMs);
  }, [debounceMs, performCheck]);

  // Set up ResizeObserver for efficient resize detection
  useEffect(() => {
    const currentElement = ref.current;
    if (!currentElement) return;

    // Initial check
    debouncedCheck();

    // Set up ResizeObserver for future changes
    resizeObserverRef.current = new ResizeObserver((entries) => {
      // Use more intelligent triggering based on content changes
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        // Only trigger if dimensions changed significantly
        if (width > threshold && height > threshold) {
          // Invalidate cache for this element
          if (currentElement) {
            measurementCache.delete(currentElement);
          }
          debouncedCheck();
          break;
        }
      }
    });

    resizeObserverRef.current.observe(currentElement);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
      // Clean up cache entry
      if (currentElement) {
        measurementCache.delete(currentElement);
      }
    };
  }, [debouncedCheck, threshold]);

  // Additional check when ref changes
  useEffect(() => {
    if (ref.current) {
      setIsLoading(true);
      debouncedCheck();
    }
  }, [debouncedCheck]);

  return { ref, isClamped, isLoading };
};
